import { motion } from "framer-motion";
import React, { SyntheticEvent } from "react";
import styled from "styled-components";
import { useLockBodyScroll } from "@utils/hooks";
import { RoundedBlock } from "@ui/components/containers/roundedBlock";
import { DeleteIcon } from "@ui/components/icons";
import { mobile } from "@ui/styles/utils";
import { CloseButton } from "./closeButton";
import { useCloseOnEscape } from "./useCloseOnEscape";

export interface ClosableModalProps {
	close: () => void;
	title?: string;
	lightTitle?: boolean;
	keyboardEventDisabled?: boolean;
	className?: string;
	layout?: boolean | "position";
	isCorpProfilePicture?: boolean;
	isStartSubscriber?: boolean;
	isModalTransaction?: boolean;
	isTransparent?: boolean;
	children?: React.ReactNode;
}
export const ClosableModal: React.FC<ClosableModalProps> = ({
	close,
	title,
	lightTitle,
	children,
	keyboardEventDisabled,
	layout,
	isCorpProfilePicture = false,
	isStartSubscriber = false,
	isModalTransaction = false,
	isTransparent = false,
	...htmlProps
}) => {
	useLockBodyScroll();

	useCloseOnEscape(close, keyboardEventDisabled);

	return (
		<Container
			{...htmlProps}
			onClick={(e: SyntheticEvent) => e.stopPropagation()}
			layout={layout}
			isCorpProfilePicture={isCorpProfilePicture}
			isStartSubscriber={isStartSubscriber}
			isTransparent={isTransparent}
			isModalTransaction={isModalTransaction}
		>
			<CloseButton onClick={() => close()} layout={layout}>
				<DeleteIcon />
			</CloseButton>
			{title && (
				<ModalTitle className="modal-title" light={lightTitle} layout={layout}>
					{title}
				</ModalTitle>
			)}
			{children}
		</Container>
	);
};

const Container = styled(motion.create(RoundedBlock)).withConfig({
	shouldForwardProp: (prop) =>
		!["isCorpProfilePicture", "isStartSubscriber", "isTransparent", "isModalTransaction"].includes(prop),
})<{
	isCorpProfilePicture?: boolean;
	isStartSubscriber?: boolean;
	isTransparent?: boolean;
	isModalTransaction?: boolean;
}>`
	border: none;
	position: relative;
	${({ isModalTransaction }) => (isModalTransaction ? `padding: 50px 4px;` : `padding: 50px 65px;`)};
	@media ${mobile} {
		padding: 50px 20px 20px;
	}
	width: 100%;
	${({ isCorpProfilePicture }) => (isCorpProfilePicture ? `max-width: 800px` : `max-width: 575px;`)};
	${({ isStartSubscriber }) => (isStartSubscriber ? `max-width: 700px` : `max-width: 575px;`)};
	${({ isModalTransaction }) => (isModalTransaction ? `max-width: 900px` : `max-width: 575px;`)};
	display: flex;
	flex-direction: column;
	align-items: stretch;
	${({ isTransparent }) => (isTransparent ? `background-color: #0000` : undefined)};
`;

export const ModalTitle = styled(motion.div)<{ light?: boolean }>`
	${({ light, theme }) => (light ? theme.textStyles.primary : theme.textStyles.title)};
`;
